import { Action, AppState } from './AppStateContext';
import { Conversation, IndividualFileChat } from '@/api/models';

// Define the reducer function
export const appStateReducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    // TOGGLE THEME
    case 'TOGGLE_THEME':
      if (state.theme === 'dark') return { ...state, theme: 'light' };
      return { ...state, theme: 'dark' };

    // SET CURRENT USER
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };

    // SET USER GROUPS
    case 'SET_USER_GROUPS':
      return { ...state, userGroups: action.payload };

    // SET SIDE PANEL OPEN/CLOSED
    case 'SET_SIDEPANEL':
      return { ...state, sidepanelOpen: action.payload };

    // SET SIDE PANEL TRANSITIONING
    case 'SET_SIDEPANEL_TRANSITIONING':
      return { ...state, sidepanelTransitioning: action.payload };
  
    // LOAD FRONTEND SETTINGS
    case 'LOAD_FRONTEND_SETTINGS':
      return { ...state, frontendSettings: action.payload };

    // LOAD NOTIFICATIONS
    case 'LOAD_NOTIFICATIONS':
      return { ...state, notifications: action.payload };

    // LOAD WEEKLY TOKEN USAGE
    case 'LOAD_USAGE':
      return { ...state, weeklyUsage: action.payload };

    // SET SEARCH POPUP OPEN
    case 'SET_SEARCH_POPUP_OPEN':
      return { ...state, searchPopupOpen: action.payload };

    // LOAD CHAT HISTORY
    case 'LOAD_CHAT_HISTORY':
      return { ...state, chatHistory: action.payload };

    // LOAD GROUP CHATS
    case 'LOAD_GROUP_CHATS': {
      // Preserve any chats already added locally (e.g. via UPDATE_GROUP_CHAT from a just-joined conversation)
      // that the server list won't know about yet
      if (!state.groupChats) return { ...state, groupChats: action.payload };
      const incomingIds = new Set((action.payload || []).map((c: Conversation) => c.id));
      const localExtra = state.groupChats.filter(c => !incomingIds.has(c.id));
      return { ...state, groupChats: [...localExtra, ...(action.payload || [])] };
    }

    // ADD GROUP CHAT
    case 'ADD_GROUP_CHAT':
      return { ...state, groupChats: [action.payload, ...(state.groupChats || [])] };

    // UPDATE GROUP CHAT
    case 'UPDATE_GROUP_CHAT': {
      if (!state.groupChats) return { ...state, groupChats: [action.payload] };
      const exists = state.groupChats.some(chat => chat.id === action.payload.id);
      if (!exists) return { ...state, groupChats: [action.payload, ...state.groupChats] };
      const updatedGroupChats = state.groupChats.map(chat =>
        chat.id === action.payload.id ? action.payload : chat
      );
      return { ...state, groupChats: updatedGroupChats };
    }

    // DELETE GROUP CHAT
    case 'DELETE_GROUP_CHAT':
      if (!state.groupChats) return state;
      return { ...state, groupChats: state.groupChats.filter(chat => chat.id !== action.payload) };

    // UPDATE CONVERSATION PIN
    case 'UPDATE_CONVERSATION_PIN': {
      if (!state.chatHistory) return { ...state, chatHistory: [] };
      const updatedChats = state.chatHistory.map(chat => {
        if (chat.id === action.payload.id) {
          return { ...chat, pinned: action.payload.pinned };
        }
        return chat;
      });
      return {
        ...state,
        currentChat: state.currentChat?.id === action.payload.id
          ? { ...state.currentChat, pinned: action.payload.pinned }
          : state.currentChat,
        chatHistory: updatedChats
      };
    }

    // UPDATE CONVERSATION TITLE
    case 'UPDATE_CONVERSATION_TITLE': {
      if (!state.chatHistory) return { ...state, chatHistory: [] };
      const updatedChats = state.chatHistory.map(chat => {
        if (chat.id === action.payload.id) {
          return { ...chat, title: action.payload.title };
        }
        return chat;
      });
      return {
        ...state,
        currentChat: state.currentChat?.id === action.payload.id
          ? { ...state.currentChat, title: action.payload.title }
          : state.currentChat,
        chatHistory: updatedChats
      };
    }

    // DELETE CONVERSATION
    case 'DELETE_CONVERSATION': {
      if (!state.chatHistory) return { ...state, chatHistory: [] };
      const filteredChat = state.chatHistory.filter(chat => chat.id !== action.payload);
      return { ...state, currentChat: null, chatHistory: filteredChat };
    }

    // LOAD CUSTOM GPTS
    case 'LOAD_CUSTOM_GPTS':
      return { ...state, customGPTs: action.payload };

    // SET CUSTOM GPTS LOADED
    case 'SET_CUSTOM_GPTS_LOADED':
      return { ...state, customGPTsLoaded: action.payload };

    // SET CURRENT MODEL
    case 'SET_CURRENT_MODEL':
      return { ...state, currentModel: action.payload };

    // SET CURRENT CUSTOM GPT
    case 'SET_CURRENT_CUSTOM_GPT':
      return { ...state, currentCustomGPT: action.payload };

    // LOAD AGENTS
    case 'LOAD_AGENTS':
      return { ...state, agents: action.payload };

    // SET CURRENT AGENT
    case 'SET_CURRENT_AGENT':
      return { ...state, currentAgent: action.payload };

    // SET CHAT PANEL TRANSITIONED
    case 'SET_CHATPANEL_TRANSITIONED':
      return { ...state, chatPanelTransitioned: action.payload };

    // UPDATE CURRENT CHAT
    case 'UPDATE_CURRENT_CHAT':
      return { ...state, currentChat: action.payload };

    // UPDATE CHAT HISTORY
    case 'UPDATE_CHAT_HISTORY': {
      if (!state.chatHistory) return state;

      const conversationIndex = state.chatHistory.findIndex(conv => conv.id === action.payload.id);
      if (conversationIndex !== -1) {
        const updatedChatHistory = [...state.chatHistory];
        updatedChatHistory[conversationIndex] = action.payload;
        return {...state, chatHistory: updatedChatHistory}
      }
      else return { ...state, chatHistory: [...state.chatHistory, action.payload] };
    }

    // SET CONV FILES OPEN
    case 'SET_CONV_FILES_OPEN':
      return { ...state, convFilesOpen: action.payload };

    // SET FILE UPLOAD
    case 'SET_FILE_UPLOAD':
      return { ...state, fileUpload: action.payload };

    // SET IMAGE UPLOAD
    case 'SET_IMAGE_UPLOAD':
      return { ...state, imageUpload: action.payload };

    // SET INDIVIDUAL FILE CHAT
    case 'SET_INDIVIDUAL_FILE_CHAT':
      return { ...state, individualFileChat: action.payload }

    // UPDATE INDIVIDUAL FILE CHAT
    case 'UPDATE_INDIVIDUAL_FILE_CHAT': {
      const newFiles: IndividualFileChat[] = action.payload ?? []
      return {
        ...state,
        individualFileChat: [
          ...(state.individualFileChat ?? []),
          ...newFiles
        ],
      }
    }

    // SET FEEDBACK STATE
    case 'SET_FEEDBACK_STATE':
      return {
        ...state,
        feedbackState: {
          ...state.feedbackState,
          [action.payload.answerId]: action.payload.feedback,
        },
      };

    // SET SELECTED CITATION
    case 'SET_SELECTED_CITATION':
      return { ...state, selectedCitation: action.payload };

    // SET SELECTED CITATIONS
    case 'SET_SELECTED_CITATIONS':
      return { ...state, selectedCitations: action.payload };

    // SET SELECTED CONVERSATION STARTER
    case 'SET_SELECTED_CONVERSATION_STARTER':
      return { ...state, selectedConvStarter: action.payload };

    // SET SELECTED TOOL
    case 'SET_SELECTED_TOOL':
      return { ...state, selectedTool: action.payload };

    case 'LOAD_PERSONALIZATION':
      return { ...state, personalization: action.payload, personalizationError: null };

    case 'SET_PERSONALIZATION_LOADING':
      return { ...state, personalizationLoading: action.payload };

    case 'SET_PERSONALIZATION_ERROR':
      return { ...state, personalizationError: action.payload };

    default:
      return state;
  }
};
